import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../ui/button';
import { 
  Eye, 
  Copy, 
  Check,
  Lock,
  Unlock,
  TrendingUp,
  Users,
  Sparkles,
  ArrowRight,
  Award,
  Target
} from 'lucide-react';

/**
 * HP.1 Gated Output: Impact Snapshot Display
 * Shows generated impact analysis with some sections locked.
 */

const GATED_FEATURES = [
  { id: 'full_impact', label: 'Complete impact analysis', icon: TrendingUp },
  { id: 'stakeholder_view', label: 'Stakeholder-ready summary', icon: Users },
  { id: 'skills_extracted', label: 'Skills demonstrated', icon: Award },
  { id: 'talking_points', label: 'Manager conversation points', icon: Target },
  { id: 'living_cv_save', label: 'Save to your Living CV', icon: Sparkles }
];

const GatedImpactDisplay = ({ 
  impactData, 
  onUnlock, 
  onStartFresh,
  accentColor = '#8b5cf6' 
}) => {
  const [copied, setCopied] = useState(false);
  const navigate = useNavigate();

  const handleCopy = async () => {
    const visibleAchievements = impactData.achievements
      .filter(a => a.visible)
      .map(a => `• ${a.title}\n  Impact: ${a.impact}`)
      .join('\n\n');
    
    const text = `
IMPACT SNAPSHOT

SUMMARY
${impactData.summary}

KEY CONTRIBUTIONS
${visibleAchievements}

[Sign up to unlock full impact analysis]
    `.trim();

    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const handleUnlock = () => {
    const returnUrl = `/visibility/complete?rv_id=${impactData.review_id}&hash=${impactData.input_hash}`;
    
    sessionStorage.setItem('rv_pending_unlock', JSON.stringify({
      review_id: impactData.review_id,
      input_hash: impactData.input_hash,
      flow_type: impactData.flow_type || 'visibility'
    }));
    
    if (onUnlock) {
      onUnlock(returnUrl);
    } else {
      navigate(`/login?return=${encodeURIComponent(returnUrl)}`);
    }
  };

  const visibleAchievements = impactData.achievements.filter(a => a.visible);
  const gatedAchievements = impactData.achievements.filter(a => !a.visible);

  return (
    <div className="max-w-3xl mx-auto" data-testid="vis-gated-impact">
      {/* Impact Card */}
      <div className="bg-white border border-slate-200 rounded-2xl shadow-xl overflow-hidden">
        {/* Header */}
        <div className="px-6 py-4 border-b border-slate-100 bg-slate-50 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div 
              className="w-10 h-10 rounded-xl flex items-center justify-center"
              style={{ backgroundColor: `${accentColor}15` }}
            >
              <Eye className="w-5 h-5" style={{ color: accentColor }} />
            </div>
            <div>
              <p className="font-semibold text-slate-900">Your Impact Snapshot</p>
              <p className="text-sm text-slate-500">Making your work visible</p>
            </div>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={handleCopy}
            className="flex items-center gap-2"
            data-testid="vis-copy-btn"
          >
            {copied ? (
              <>
                <Check className="w-4 h-4 text-green-500" />
                Copied!
              </>
            ) : (
              <>
                <Copy className="w-4 h-4" />
                Copy
              </>
            )}
          </Button>
        </div>

        {/* Content */}
        <div className="p-6">
          {/* Summary */}
          <div className="mb-6">
            <h4 className="text-xs uppercase tracking-wider text-slate-500 mb-2 font-medium">The Story Your Manager Should Hear</h4>
            <p className="text-slate-800 leading-relaxed">{impactData.summary}</p>
          </div>

          {/* Visible Achievements */}
          <div className="mb-6">
            <h4 className="text-xs uppercase tracking-wider text-slate-500 mb-3 font-medium flex items-center gap-2">
              <Award className="w-4 h-4" />
              Key Contributions
            </h4>
            
            <div className="space-y-3">
              {visibleAchievements.map((achievement, index) => (
                <div 
                  key={index}
                  className="bg-gradient-to-r from-violet-50 to-slate-50 rounded-xl p-4 border border-violet-100"
                  data-testid={`vis-achievement-${index}`}
                >
                  <p className="font-medium text-slate-900 mb-1">{achievement.title}</p>
                  <p className="text-sm text-slate-600 mb-2">{achievement.impact}</p>
                  <div className="flex flex-wrap gap-1.5">
                    {achievement.skills.map((skill, i) => (
                      <span 
                        key={i}
                        className="px-2 py-0.5 bg-white border border-violet-200 rounded text-xs text-violet-700"
                      >
                        {skill}
                      </span>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Gated Section */}
          <div className="relative">
            {/* Blurred gated achievements */}
            {gatedAchievements.length > 0 && (
              <div className="space-y-3 mb-4">
                {gatedAchievements.slice(0, 2).map((achievement, index) => (
                  <div 
                    key={index}
                    className="bg-slate-50 rounded-xl p-4 border border-slate-100 relative overflow-hidden"
                  >
                    <div className="blur-sm select-none">
                      <p className="font-medium text-slate-900 mb-1">{achievement.title}</p>
                      <p className="text-sm text-slate-600 mb-2">
                        {achievement.impact.substring(0, 50)}...
                      </p>
                    </div>
                    <div className="absolute inset-0 bg-gradient-to-b from-transparent via-white/60 to-white/90 flex items-center justify-center">
                      <Lock className="w-5 h-5 text-slate-400" />
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Who Benefited - Partial */}
            <div className="mb-4">
              <h4 className="text-xs uppercase tracking-wider text-slate-500 mb-2 font-medium flex items-center gap-2">
                <Users className="w-4 h-4" />
                Who Benefited
              </h4>
              <div className="space-y-2">
                {impactData.development_areas?.items?.slice(0, 1).map((area, i) => (
                  <div key={i} className="flex items-start gap-2 text-sm text-slate-700">
                    <span className="text-violet-400 mt-0.5">•</span>
                    <span>{area}</span>
                  </div>
                ))}
                {(impactData.development_areas?.gated_count || 0) > 0 && (
                  <div className="flex items-center gap-2 text-sm text-slate-400 blur-sm select-none">
                    <Lock className="w-3 h-3" />
                    <span>+{impactData.development_areas.gated_count} more insights locked</span>
                  </div>
                )}
              </div>
            </div>

            {/* Skills Demonstrated - Partial */}
            <div className="mb-6">
              <h4 className="text-xs uppercase tracking-wider text-slate-500 mb-2 font-medium flex items-center gap-2">
                <Sparkles className="w-4 h-4" />
                Skills Demonstrated
              </h4>
              <div className="space-y-2">
                {impactData.goals?.items?.slice(0, 1).map((goal, i) => (
                  <div key={i} className="flex items-start gap-2 text-sm text-slate-700">
                    <span className="text-violet-400 mt-0.5">•</span>
                    <span>{goal}</span>
                  </div>
                ))}
                {(impactData.goals?.gated_count || 0) > 0 && (
                  <div className="flex items-center gap-2 text-sm text-slate-400 blur-sm select-none">
                    <Lock className="w-3 h-3" />
                    <span>+{impactData.goals.gated_count} more skills locked</span>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Unlock CTA Section */}
        <div className="px-6 py-6 bg-gradient-to-br from-violet-900 to-slate-900 text-white">
          <div className="text-center mb-6">
            <Unlock className="w-8 h-8 mx-auto mb-3 text-white/80" />
            <h3 className="text-lg font-semibold mb-2">Make Your Work Visible</h3>
            <p className="text-violet-200 text-sm">
              Unlock the full impact analysis and save it to your Living CV
            </p>
          </div>

          {/* Features to unlock */}
          <div className="grid grid-cols-2 gap-3 mb-6">
            {GATED_FEATURES.slice(0, 4).map((feature) => {
              const Icon = feature.icon;
              return (
                <div 
                  key={feature.id}
                  className="flex items-center gap-2 px-3 py-2 bg-white/10 rounded-lg text-sm"
                >
                  <Icon className="w-4 h-4 text-white/70 flex-shrink-0" />
                  <span className="text-white/90 text-xs">{feature.label}</span>
                </div>
              );
            })}
          </div>

          {/* CTA Buttons */}
          <div className="flex flex-col sm:flex-row gap-3">
            <Button
              onClick={handleUnlock}
              className="flex-1 py-3 text-white font-medium"
              style={{ backgroundColor: accentColor }}
              data-testid="vis-unlock-btn"
            >
              <Unlock className="w-4 h-4 mr-2" />
              Unlock Full Analysis (Free)
            </Button>
            
            {onStartFresh && (
              <Button
                variant="outline"
                onClick={onStartFresh}
                className="flex-1 py-3 text-white border-white/30 hover:bg-white/10"
                data-testid="vis-start-fresh-btn"
              >
                Start Fresh
              </Button>
            )}
          </div>

          <p className="text-center text-xs text-violet-300 mt-4">
            Already have an account?{' '}
            <button 
              onClick={() => navigate('/login')}
              className="text-white underline hover:no-underline"
            >
              Sign in
            </button>
          </p>
        </div>
      </div>

      {/* Expiry notice */}
      {impactData.expires_at && (
        <p className="text-center text-sm text-slate-500 mt-4">
          This snapshot will be available for 1 hour. Sign up to save it permanently.
        </p>
      )}
    </div>
  );
};

export default GatedImpactDisplay;
